import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { motion } from 'framer-motion';
import { 
  Loader2, 
  Users, 
  Calendar,
  Download,
  Trash2,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Phone,
  Mail,
  Euro,
  UserCheck,
  UserX,
  FileText
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminInicicioAnyos = () => {
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [privateRequests, setPrivateRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSession, setExpandedSession] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch sessions summary
      const sessionsRes = await authFetch(`${API_URL}/api/iniciacio-anyos/admin/sessions-resum`, {
        
      });
      if (sessionsRes.ok) {
        const data = await sessionsRes.json();
        setSessions(data);
      }

      // Fetch stats
      const statsRes = await authFetch(`${API_URL}/api/iniciacio-anyos/admin/stats`, {
        
      });
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }

      // Fetch private requests
      const privateRes = await authFetch(`${API_URL}/api/iniciacio-anyos/admin/registres?tipus=privada`, {
        
      });
      if (privateRes.ok) {
        setPrivateRequests(await privateRes.json());
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePayment = async (registreId, nouEstat) => {
    setUpdatingId(registreId);
    try {
      const response = await fetch(
        `${API_URL}/api/iniciacio-anyos/admin/registre/${registreId}/pagament?estat=${nouEstat}`,
        {
          method: 'PUT',
          
        }
      );
      if (response.ok) {
        fetchData();
      }
    } catch (err) {
      console.error('Error updating payment:', err);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (registreId) => {
    if (!window.confirm('Segur que vols eliminar aquesta inscripció?')) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/iniciacio-anyos/admin/registre/${registreId}`,
        {
          method: 'DELETE',
          
        }
      );
      if (response.ok) {
        fetchData();
      }
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  const handleExportCSV = () => {
    window.open(`${API_URL}/api/iniciacio-anyos/admin/export-csv`, '_blank');
  };

  const toggleSession = (data) => {
    setExpandedSession(expandedSession === data ? null : data);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-iniciacio-anyos">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1A4D2E]">Iniciació Anyós Park</h1>
            <p className="text-gray-600">Gestió d'inscripcions i sessions</p>
          </div>
          <button
            onClick={handleExportCSV}
            className="inline-flex items-center gap-2 bg-[#1A4D2E] text-white px-4 py-2 rounded-lg hover:bg-[#153d24] transition-colors"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" />
            Exportar CSV
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#1A4D2E]/10 rounded-full flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#1A4D2E]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1A4D2E]">{stats.total_inscripcions_grup}</p>
                  <p className="text-xs text-gray-500">Inscripcions grup</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#D4AF37]">{stats.socis_anyos_park}</p>
                  <p className="text-xs text-gray-500">Socis Anyós Park</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Euro className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{stats.pagats}</p>
                  <p className="text-xs text-gray-500">Pagats</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-orange-600">{stats.pendents_pagament}</p>
                  <p className="text-xs text-gray-500">Pendents pagament</p>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Sessions List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-semibold text-[#1A4D2E] flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Sessions de Grup (Diumenges 10h)
            </h2>
          </div>
          
          <div className="divide-y divide-gray-100">
            {sessions.filter(s => s.inscrits > 0 || new Date(s.data) >= new Date()).slice(0, 12).map((session) => (
              <div key={session.data} className="hover:bg-gray-50 transition-colors">
                <button
                  onClick={() => toggleSession(session.data)}
                  className="w-full p-4 flex items-center justify-between text-left"
                  data-testid={`session-toggle-${session.data}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      session.complet ? 'bg-red-100' : session.inscrits > 0 ? 'bg-[#1A4D2E]/10' : 'bg-gray-100'
                    }`}>
                      <Calendar className={`w-5 h-5 ${
                        session.complet ? 'text-red-600' : session.inscrits > 0 ? 'text-[#1A4D2E]' : 'text-gray-400'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{session.data_display}</p>
                      <p className="text-sm text-gray-500">
                        {session.inscrits} / 8 inscrits
                        {session.complet && <span className="ml-2 text-red-600 font-semibold">COMPLET</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${session.complet ? 'bg-red-500' : 'bg-[#1A4D2E]'}`}
                        style={{ width: `${(session.inscrits / 8) * 100}%` }}
                      />
                    </div>
                    {expandedSession === session.data ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Expanded participants list */}
                {expandedSession === session.data && session.participants.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="bg-gray-50 px-4 pb-4"
                  >
                    <div className="space-y-2">
                      {session.participants.map((participant, idx) => (
                        <div 
                          key={participant._id}
                          className="bg-white rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium text-gray-900">{participant.nom}</p>
                              {participant.soci_anyos ? (
                                <span className="bg-[#D4AF37]/20 text-[#D4AF37] text-xs px-2 py-0.5 rounded-full font-medium">
                                  Soci Anyós
                                </span>
                              ) : (
                                <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                                  No soci
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {participant.telefon}
                              </span>
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {participant.email}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <select
                              value={participant.estat_pagament}
                              onChange={(e) => handleUpdatePayment(participant._id, e.target.value)}
                              disabled={updatingId === participant._id}
                              className={`text-sm rounded-lg px-3 py-2 border ${
                                participant.estat_pagament === 'PAGAT' 
                                  ? 'bg-green-50 border-green-200 text-green-700'
                                  : 'bg-orange-50 border-orange-200 text-orange-700'
                              }`}
                              data-testid={`payment-select-${participant._id}`}
                            >
                              <option value="PAGAMENT AL CAMP">Pendent</option>
                              <option value="PAGAT">Pagat</option>
                            </select>
                            
                            <button
                              onClick={() => handleDelete(participant._id)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Eliminar inscripció"
                              data-testid={`delete-btn-${participant._id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {expandedSession === session.data && session.participants.length === 0 && (
                  <div className="bg-gray-50 px-4 pb-4">
                    <p className="text-gray-500 text-sm text-center py-4">
                      Cap inscripció per aquesta sessió
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Private Session Requests */}
        {privateRequests.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h2 className="font-semibold text-[#1A4D2E] flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Sol·licituds de Sessió Privada ({privateRequests.length})
              </h2>
            </div>
            
            <div className="divide-y divide-gray-100">
              {privateRequests.map((request) => (
                <div key={request._id} className="p-4">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <p className="font-medium text-gray-900">{request.nom}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          request.estat === 'PENDENT CONFIRMACIÓ'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {request.estat}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-2">
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {request.telefon}
                        </span>
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {request.email}
                        </span>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                        <p className="font-medium text-gray-700 mb-1">Disponibilitat:</p>
                        {request.comentaris}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleDelete(request._id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar sol·licitud"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminInicicioAnyos;
